import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Badge, Box, Button, Chip, CircularProgress,
  Divider, Drawer, FormControl, Grid, IconButton, InputAdornment,
  InputLabel, LinearProgress, MenuItem, Pagination, Paper, Select,
  Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { customerDatabaseApi } from '../api/customerDatabaseApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  hot:      { bg: '#fce4ec', text: '#b71c1c' },
  warm:     { bg: '#fff3e0', text: '#e65100' },
  cold:     { bg: '#e3f2fd', text: '#1565c0' },
  urgent:   { bg: '#b71c1c', text: '#fff' },
  inactive: { bg: '#f5f5f5', text: '#757575' },
  converted:{ bg: '#e8f5e9', text: '#2e7d32' },
};

const STAGE_COLORS = {
  customer: { bg: '#e8f5e9', text: '#2e7d32' },
  prospect: { bg: '#e3f2fd', text: '#1565c0' },
  lead:     { bg: '#fff3e0', text: '#e65100' },
  contact:  { bg: '#f5f5f5', text: '#757575' },
};

const ORG_TYPE_CONFIG = {
  individual:   { label: 'Individual',    color: '#1565c0', bg: '#e3f2fd' },
  corporate:    { label: 'Corporate',     color: '#4a148c', bg: '#f3e5f5' },
  school:       { label: 'School',        color: '#1b5e20', bg: '#e8f5e9' },
  church:       { label: 'Church',        color: '#bf360c', bg: '#fbe9e7' },
  government:   { label: 'Government',    color: '#0d47a1', bg: '#e8eaf6' },
  ngo:          { label: 'NGO / Charity', color: '#e65100', bg: '#fff3e0' },
  travel_agent: { label: 'Travel Agent',  color: '#006064', bg: '#e0f7fa' },
  supplier:     { label: 'Supplier',      color: '#37474f', bg: '#eceff1' },
  unknown:      { label: 'Unknown',       color: '#757575', bg: '#f5f5f5' },
};

function OrgTypeBadge({ orgType }) {
  if (!orgType || orgType === 'unknown') return <Typography variant="caption" color="text.disabled">—</Typography>;
  const cfg = ORG_TYPE_CONFIG[orgType] || ORG_TYPE_CONFIG.unknown;
  return (
    <Chip label={cfg.label} size="small"
      sx={{ fontSize: 10, fontWeight: 600, bgcolor: cfg.bg, color: cfg.color }} />
  );
}

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function fmt(val, prefix = '', suffix = '') {
  if (val === null || val === undefined || val === 0) return '—';
  return `${prefix}${val}${suffix}`;
}

function fmtCurrency(val, currency = 'USD') {
  if (!val) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', minimumFractionDigits: 0 }).format(val);
}

function timeAgo(iso) {
  if (!iso) return '—';
  const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (secs < 60)   return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60)   return `${mins}m ago`;
  const hrs  = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)   return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function ScoreBar({ value, color }) {
  const c = color ?? (value >= 70 ? 'success' : value >= 40 ? 'primary' : 'warning');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={value ?? 0}
        color={c}
        sx={{ width: 60, height: 6, borderRadius: 3, flexShrink: 0 }}
      />
      <Typography variant="caption" fontWeight={600}>{value ?? 0}</Typography>
    </Box>
  );
}

function CopyChip({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <Chip
      label={value}
      size="small"
      variant="outlined"
      deleteIcon={<ContentCopyIcon sx={{ fontSize: 12 }} />}
      onDelete={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      sx={{ fontSize: 12, maxWidth: 240, borderColor: copied ? '#2e7d32' : undefined }}
    />
  );
}

// ── Header stats bar ──────────────────────────────────────────────────────────

function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { label: 'Total Profiles',  value: stats.total_profiles?.toLocaleString(), color: '#1565c0' },
    { label: 'Customers',       value: stats.customers?.toLocaleString(),       color: '#2e7d32' },
    { label: 'Prospects',       value: stats.prospects?.toLocaleString(),        color: '#e65100' },
    { label: 'At Risk',         value: stats.at_risk?.toLocaleString(),         color: '#b71c1c' },
    { label: 'Hot Clients',     value: stats.hot_clients?.toLocaleString(),     color: '#b71c1c' },
    { label: 'Total Won',       value: fmtCurrency(stats.total_won_value),      color: '#2e7d32' },
    { label: 'Pipeline',        value: fmtCurrency(stats.total_pipeline),       color: '#1565c0' },
    { label: 'Possible Dupes',  value: stats.possible_duplicates?.toLocaleString(), color: '#f57f17' },
  ];
  return (
    <Grid container spacing={2} mb={3}>
      {items.map((item) => (
        <Grid item xs={6} sm={3} md={1.5} key={item.label}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color={item.color}>{item.value ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// ── Profile detail drawer ─────────────────────────────────────────────────────

function ProfileDrawer({ contactId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);

  useEffect(() => {
    if (!contactId) { setProfile(null); return; }
    setLoading(true);
    setDrawerTab(0);
    customerDatabaseApi.getProfile(contactId)
      .then(setProfile)
      .finally(() => setLoading(false));
  }, [contactId]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const res = await customerDatabaseApi.rebuildProfile(contactId);
      if (res.profile) setProfile(res.profile);
    } finally { setRebuilding(false); }
  };

  const p = profile;

  return (
    <Drawer anchor="right" open={Boolean(contactId)} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 580 }, p: 0 } }}>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && p && (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 3, bgcolor: '#1a237e', color: '#fff' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ bgcolor: '#3949ab', width: 48, height: 48, fontSize: 20, fontWeight: 700 }}>
                    {(p.display_name || '?')[0].toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#fff">{p.display_name ?? 'Unknown'}</Typography>
                    {p.company && <Typography variant="caption" sx={{ color: '#c5cae9' }}>{p.company}</Typography>}
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" gap={0.5}>
                  {p.operational_status && (
                    <Chip label={p.operational_status} size="small"
                      sx={{ bgcolor: STATUS_COLORS[p.operational_status]?.bg ?? '#eee', color: STATUS_COLORS[p.operational_status]?.text ?? '#333', fontWeight: 700, fontSize: 11 }} />
                  )}
                  {p.relationship_stage && (
                    <Chip label={p.relationship_stage} size="small"
                      sx={{ bgcolor: STAGE_COLORS[p.relationship_stage]?.bg, color: STAGE_COLORS[p.relationship_stage]?.text, fontWeight: 600, fontSize: 11 }} />
                  )}
                  {p.org_type && p.org_type !== 'unknown' && (() => {
                    const cfg = ORG_TYPE_CONFIG[p.org_type] || ORG_TYPE_CONFIG.unknown;
                    return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: 11 }} />;
                  })()}
                  {p.is_hot_client && <Chip icon={<LocalFireDepartmentIcon sx={{ fontSize: 13 }} />} label="Hot" size="small" sx={{ bgcolor: '#b71c1c', color: '#fff', fontWeight: 700, fontSize: 11 }} />}
                  {p.is_at_risk && <Chip icon={<WarningAmberIcon sx={{ fontSize: 13 }} />} label="At Risk" size="small" sx={{ bgcolor: '#f57f17', color: '#fff', fontWeight: 700, fontSize: 11 }} />}
                </Stack>
              </Box>
              <IconButton size="small" onClick={handleRebuild} disabled={rebuilding} sx={{ color: '#c5cae9' }}>
                {rebuilding ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Stack>
          </Box>

          {/* Contact identity row */}
          <Box sx={{ px: 3, py: 1.5, bgcolor: '#f8f9fe', borderBottom: '1px solid #e0e0e0' }}>
            <Stack spacing={0.5}>
              {(p.all_emails ?? []).map((e) => (
                <Stack key={e} direction="row" spacing={1} alignItems="center">
                  <EmailIcon sx={{ fontSize: 14, color: '#5c6bc0' }} />
                  <CopyChip value={e} />
                </Stack>
              ))}
              {(p.all_phones ?? []).map((ph) => (
                <Stack key={ph} direction="row" spacing={1} alignItems="center">
                  <PhoneIcon sx={{ fontSize: 14, color: '#5c6bc0' }} />
                  <CopyChip value={ph} />
                </Stack>
              ))}
              {p.assigned_consultant && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <BusinessIcon sx={{ fontSize: 14, color: '#5c6bc0' }} />
                  <Typography variant="caption" color="text.secondary">Consultant: <strong>{p.assigned_consultant}</strong></Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          {/* Tabs */}
          <Tabs value={drawerTab} onChange={(_, v) => setDrawerTab(v)}
            sx={{ px: 2, borderBottom: '1px solid #e0e0e0', minHeight: 40 }}
            TabIndicatorProps={{ style: { height: 2 } }}>
            <Tab label="Overview"   sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
            <Tab label="Sales"      sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
            <Tab label="Engagement" sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
            <Tab label="History"    sx={{ fontSize: 12, minHeight: 40, py: 0 }} />
          </Tabs>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
            {/* Overview */}
            {drawerTab === 0 && (
              <Stack spacing={2}>
                <DrawerSection title="Scores">
                  <DrawerRow label="Readiness"          value={<ScoreBar value={p.buying_readiness_score} />} />
                  <DrawerRow label="Engagement"         value={<ScoreBar value={p.engagement_score} />} />
                  <DrawerRow label="Relationship Depth" value={<ScoreBar value={p.relationship_depth_score} />} />
                  <DrawerRow label="Churn Risk"         value={<ScoreBar value={p.churn_risk_score} color={p.churn_risk_score >= 65 ? 'error' : 'success'} />} />
                  <DrawerRow label="Est. Lifetime Value" value={<Typography variant="body2" fontWeight={700} color="#2e7d32">{fmtCurrency(p.lifetime_value_estimate, p.quote_currency)}</Typography>} />
                </DrawerSection>
                <DrawerSection title="Customer Profile">
                  <DrawerRow label="Primary Interest"   value={p.primary_interest} />
                  <DrawerRow label="Organisation Type"  value={<OrgTypeBadge orgType={p.org_type} />} />
                  <DrawerRow label="Preferred Channel"  value={p.preferred_channel} />
                  <DrawerRow label="Preferred Service"  value={p.preferred_service_type} />
                  <DrawerRow label="Top Destinations"   value={(p.top_destinations ?? []).join(', ') || null} />
                  <DrawerRow label="Last Active"        value={timeAgo(p.last_active_at)} />
                  <DrawerRow label="Relationship"       value={`${p.relationship_days} days`} />
                  <DrawerRow label="Dominant Intent"    value={p.dominant_inbound_intent} />
                  <DrawerRow label="Dominant Sentiment" value={p.dominant_sentiment} />
                </DrawerSection>
                {p.ai_summary && (
                  <DrawerSection title="AI Summary">
                    <Typography variant="body2" color="text.secondary">{p.ai_summary}</Typography>
                  </DrawerSection>
                )}
                {p.recommended_next_action && (
                  <Alert severity="info" sx={{ fontSize: 12 }}>{p.recommended_next_action}</Alert>
                )}
                {(p.active_tags ?? []).length > 0 && (
                  <DrawerSection title="Intelligence Tags">
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {p.active_tags.map((t) => (
                        <Chip key={t} label={t} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      ))}
                    </Stack>
                  </DrawerSection>
                )}
                {(p.possible_duplicates ?? []).length > 0 && (
                  <Alert severity="warning" sx={{ fontSize: 12 }}>
                    {p.possible_duplicates.length} possible duplicate contact(s) detected (IDs: {p.possible_duplicates.join(', ')})
                  </Alert>
                )}
              </Stack>
            )}

            {/* Sales */}
            {drawerTab === 1 && (
              <Stack spacing={2}>
                <DrawerSection title="Quote Pipeline">
                  <DrawerRow label="Quotes Created" value={p.total_quotes_created} />
                  <DrawerRow label="Quotes Sent"    value={p.total_quotes_sent} />
                  <DrawerRow label="Won"            value={<Typography variant="body2" fontWeight={700} color="#2e7d32">{p.total_quotes_won}</Typography>} />
                  <DrawerRow label="Lost"           value={p.total_quotes_lost} />
                  <DrawerRow label="Win Rate"       value={`${p.win_rate ?? 0}%`} />
                  <DrawerRow label="Won Value"      value={<Typography variant="body2" fontWeight={700} color="#2e7d32">{fmtCurrency(p.won_value, p.quote_currency)}</Typography>} />
                  <DrawerRow label="Open Pipeline"  value={fmtCurrency(p.pipeline_value, p.quote_currency)} />
                  <DrawerRow label="Avg Quote"      value={fmtCurrency(p.avg_quote_value, p.quote_currency)} />
                  <DrawerRow label="Last Quote"     value={timeAgo(p.last_quote_at)} />
                  <DrawerRow label="Last Win"       value={timeAgo(p.last_won_at)} />
                </DrawerSection>
                <DrawerSection title="Inquiries">
                  <DrawerRow label="Total Inquiries"  value={p.total_inquiries} />
                  <DrawerRow label="First Inquiry"    value={timeAgo(p.first_inquiry_at)} />
                  <DrawerRow label="Last Inquiry"     value={timeAgo(p.last_inquiry_at)} />
                  {Object.keys(p.inquiry_categories ?? {}).length > 0 && (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Categories</Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {Object.entries(p.inquiry_categories).map(([cat, cnt]) => (
                          <Chip key={cat} label={`${cat}: ${cnt}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </DrawerSection>
                {(p.top_quote_destinations ?? []).length > 0 && (
                  <DrawerSection title="Destinations Quoted">
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {p.top_quote_destinations.map((d) => <Chip key={d} label={d} size="small" sx={{ fontSize: 11 }} />)}
                    </Stack>
                  </DrawerSection>
                )}
              </Stack>
            )}

            {/* Engagement */}
            {drawerTab === 2 && (
              <Stack spacing={2}>
                <DrawerSection title="Campaign Email">
                  <DrawerRow label="Campaigns Received" value={p.total_campaigns_received} />
                  <DrawerRow label="Opened"             value={p.total_campaigns_opened} />
                  <DrawerRow label="Clicked"            value={p.total_campaigns_clicked} />
                  <DrawerRow label="Open Rate"          value={`${p.email_open_rate}%`} />
                  <DrawerRow label="Click Rate"         value={`${p.email_click_rate}%`} />
                  <DrawerRow label="Last Opened"        value={timeAgo(p.last_campaign_opened_at)} />
                  <DrawerRow label="Last Clicked"       value={timeAgo(p.last_campaign_clicked_at)} />
                  {p.best_open_hour != null && (
                    <DrawerRow label="Best Time to Send" value={`${p.best_open_hour}:00 on ${DAYS[p.best_open_day] ?? '—'}`} />
                  )}
                </DrawerSection>
                <DrawerSection title="Inbound Emails">
                  <DrawerRow label="Total Inbound"   value={p.total_inbound_emails} />
                  <DrawerRow label="Last Inbound"    value={timeAgo(p.last_inbound_email_at)} />
                  <DrawerRow label="Main Intent"     value={p.dominant_inbound_intent} />
                  <DrawerRow label="Sentiment"       value={p.dominant_sentiment} />
                </DrawerSection>
                <DrawerSection title="Conversations">
                  <DrawerRow label="Total Conversations" value={p.total_conversations} />
                  <DrawerRow label="Last Conversation"   value={timeAgo(p.last_conversation_at)} />
                  <DrawerRow label="Preferred Channel"   value={p.preferred_channel} />
                </DrawerSection>
              </Stack>
            )}

            {/* History */}
            {drawerTab === 3 && (
              <Stack spacing={2}>
                <DrawerSection title="All Known Emails">
                  {(p.all_emails ?? []).length > 0
                    ? p.all_emails.map((e) => <CopyChip key={e} value={e} />)
                    : <Typography variant="caption" color="text.secondary">None recorded</Typography>}
                </DrawerSection>
                <DrawerSection title="All Known Phone Numbers">
                  {(p.all_phones ?? []).length > 0
                    ? p.all_phones.map((ph) => <CopyChip key={ph} value={ph} />)
                    : <Typography variant="caption" color="text.secondary">None recorded</Typography>}
                </DrawerSection>
                <DrawerSection title="Travel Dates Mentioned">
                  {(p.travel_dates_mentioned ?? []).length > 0
                    ? <Stack direction="row" flexWrap="wrap" gap={0.5}>{p.travel_dates_mentioned.map((d) => <Chip key={d} label={d} size="small" variant="outlined" sx={{ fontSize: 11 }} />)}</Stack>
                    : <Typography variant="caption" color="text.secondary">None detected</Typography>}
                </DrawerSection>
                <DrawerSection title="Timeline">
                  <DrawerRow label="First Seen"     value={timeAgo(p.first_seen_at)} />
                  <DrawerRow label="First Inquiry"  value={timeAgo(p.first_inquiry_at)} />
                  <DrawerRow label="Last Active"    value={timeAgo(p.last_active_at)} />
                  <DrawerRow label="Last Won Deal"  value={timeAgo(p.last_won_at)} />
                  <DrawerRow label="Profile Built"  value={timeAgo(p.profile_built_at)} />
                  <DrawerRow label="Build Version"  value={`v${p.build_version}`} />
                </DrawerSection>
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}

function DrawerSection({ title, children }) {
  return (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        textTransform="uppercase" letterSpacing={0.8}>{title}</Typography>
      <Paper variant="outlined" sx={{ p: 1.5, mt: 0.75 }}>
        <Stack spacing={0.75}>{children}</Stack>
      </Paper>
    </Box>
  );
}

function DrawerRow({ label, value }) {
  const display = typeof value === 'string' || typeof value === 'number'
    ? <Typography variant="body2" color="text.primary">{value || '—'}</Typography>
    : value ?? <Typography variant="body2" color="text.disabled">—</Typography>;
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130 }}>{label}</Typography>
      {display}
    </Stack>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CustomerDatabasePage() {
  const [stats, setStats]             = useState(null);
  const [results, setResults]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [loadError, setLoadError]     = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [orgTypeFilter, setOrgTypeFilter] = useState('');
  const [sortBy, setSortBy]           = useState('last_active_at');
  const [page, setPage]               = useState(1);
  const [selectedId, setSelectedId]   = useState(null);
  const debounceRef = useRef(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats(await customerDatabaseApi.getStats()); }
    finally { setStatsLoading(false); }
  }, []);

  const loadResults = useCallback(async (params) => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await customerDatabaseApi.search(params);
      setResults(data);
    } catch (err) {
      setLoadError(err?.response?.data?.message ?? err?.message ?? 'Failed to load profiles.');
    } finally { setLoading(false); }
  }, []);

  // Initial load
  useEffect(() => {
    loadStats();
    loadResults({ page: 1, per_page: 20, sort: 'last_active_at' });
  }, [loadStats, loadResults]);

  // Re-query on filter/search change (debounced)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      loadResults({ search, status: statusFilter, stage: stageFilter, org_type: orgTypeFilter, sort: sortBy, page: 1 });
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, statusFilter, stageFilter, orgTypeFilter, sortBy, loadResults]);

  // Page change
  useEffect(() => {
    loadResults({ search, status: statusFilter, stage: stageFilter, org_type: orgTypeFilter, sort: sortBy, page });
  }, [page]); // eslint-disable-line

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Page header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Customer Database</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            Every customer in one place — searched by email, phone, name, or company
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={statsLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={loadStats}
          disabled={statsLoading}
        >
          Refresh Stats
        </Button>
      </Stack>

      {/* Stats bar */}
      {statsLoading
        ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        : <StatsBar stats={stats} />
      }

      {/* Search + filters */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search by name, email, phone, company…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 320 }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {['hot','warm','cold','urgent','inactive','converted'].map((s) => (
              <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Stage</InputLabel>
          <Select value={stageFilter} label="Stage" onChange={(e) => setStageFilter(e.target.value)}>
            <MenuItem value="">All stages</MenuItem>
            <MenuItem value="customer">Customer (won deal)</MenuItem>
            <MenuItem value="prospect">Prospect (quoted)</MenuItem>
            <MenuItem value="lead">Lead (inquired)</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Org Type</InputLabel>
          <Select value={orgTypeFilter} label="Org Type" onChange={(e) => setOrgTypeFilter(e.target.value)}>
            <MenuItem value="">All org types</MenuItem>
            {Object.entries(ORG_TYPE_CONFIG)
              .filter(([v]) => v !== 'unknown')
              .map(([v, c]) => (
                <MenuItem key={v} value={v}>{c.label}</MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Sort by</InputLabel>
          <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="last_active_at">Last Active</MenuItem>
            <MenuItem value="churn_risk_score">Churn Risk (High→Low)</MenuItem>
            <MenuItem value="won_value">Won Value (High→Low)</MenuItem>
            <MenuItem value="buying_readiness_score">Readiness</MenuItem>
            <MenuItem value="relationship_depth_score">Relationship Depth</MenuItem>
            <MenuItem value="engagement_score">Engagement Score</MenuItem>
          </Select>
        </FormControl>

        {loading && <CircularProgress size={20} />}
      </Stack>

      {/* Results table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ minWidth: 220 }}><Typography variant="caption" fontWeight={700}>Customer</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700}>Contact</Typography></TableCell>
              <TableCell align="center"><Typography variant="caption" fontWeight={700}>Status</Typography></TableCell>
              <TableCell align="center"><Typography variant="caption" fontWeight={700}>Stage</Typography></TableCell>
              <TableCell align="center"><Typography variant="caption" fontWeight={700}>Org Type</Typography></TableCell>
              <TableCell align="center"><Typography variant="caption" fontWeight={700}>Readiness</Typography></TableCell>
              <TableCell align="right"><Typography variant="caption" fontWeight={700}>Won Value</Typography></TableCell>
              <TableCell align="right"><Typography variant="caption" fontWeight={700}>LTV Est.</Typography></TableCell>
              <TableCell align="center"><Typography variant="caption" fontWeight={700}>Churn Risk</Typography></TableCell>
              <TableCell align="center"><Typography variant="caption" fontWeight={700}>Open Rate</Typography></TableCell>
              <TableCell><Typography variant="caption" fontWeight={700}>Last Active</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}

            {!loading && (results?.data ?? []).map((p) => {
              const statusColors = STATUS_COLORS[p.operational_status] ?? { bg: '#f5f5f5', text: '#555' };
              const stageColors  = STAGE_COLORS[p.relationship_stage]  ?? { bg: '#f5f5f5', text: '#555' };
              return (
                <TableRow
                  key={p.contact_id}
                  hover
                  onClick={() => setSelectedId(p.contact_id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 28, height: 28, bgcolor: '#3f51b5', fontSize: 12 }}>
                        {(p.display_name || '?')[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {p.display_name ?? '—'}
                          {p.is_hot_client && <LocalFireDepartmentIcon sx={{ fontSize: 13, color: '#b71c1c', ml: 0.5 }} />}
                          {p.is_at_risk && <WarningAmberIcon sx={{ fontSize: 13, color: '#f57f17', ml: 0.5 }} />}
                        </Typography>
                        {p.company && <Typography variant="caption" color="text.secondary">{p.company}</Typography>}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      {p.primary_email && <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.primary_email}</Typography>}
                      {p.primary_phone && <Typography variant="caption" color="text.secondary">{p.primary_phone}</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    {p.operational_status && (
                      <Chip label={p.operational_status} size="small"
                        sx={{ bgcolor: statusColors.bg, color: statusColors.text, fontWeight: 600, fontSize: 10 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {p.relationship_stage && (
                      <Chip label={p.relationship_stage} size="small"
                        sx={{ bgcolor: stageColors.bg, color: stageColors.text, fontWeight: 600, fontSize: 10 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <OrgTypeBadge orgType={p.org_type} />
                  </TableCell>
                  <TableCell align="center">
                    <ScoreBar value={p.buying_readiness_score ?? 0} />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} color="#2e7d32">
                      {p.won_value > 0 ? fmtCurrency(p.won_value, p.quote_currency) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="#1565c0">
                      {p.lifetime_value_estimate > 0 ? fmtCurrency(p.lifetime_value_estimate, p.quote_currency) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={p.churn_risk_score ?? 0}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: 11,
                        bgcolor: (p.churn_risk_score ?? 0) >= 65 ? '#fce4ec' : (p.churn_risk_score ?? 0) >= 40 ? '#fff3e0' : '#e8f5e9',
                        color:   (p.churn_risk_score ?? 0) >= 65 ? '#b71c1c' : (p.churn_risk_score ?? 0) >= 40 ? '#e65100' : '#2e7d32',
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{p.email_open_rate > 0 ? `${p.email_open_rate}%` : '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{timeAgo(p.last_active_at)}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}

            {!loading && loadError && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                  <Typography color="error" variant="body2">{loadError}</Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && !loadError && (results?.data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" variant="body2">
                    {search || statusFilter || stageFilter
                      ? 'No customers match your search. Try different terms.'
                      : 'No profiles yet. Run: php artisan intelligence:build-unified-profiles'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {(results?.last_page ?? 0) > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={results.last_page} page={page} onChange={(_, v) => setPage(v)} color="primary" />
        </Box>
      )}

      <ProfileDrawer
        contactId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </Box>
  );
}
